import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import debounce from "lodash.debounce";
import { useCalendar } from "./../../context/CalendarProvider";
import {
  Day,
  SchedulerData,
  SchedulerProjectData,
  TooltipData,
  ZoomLevel,
} from "./../../types/global";
import { getTooltipData } from "./../../utils/getTooltipData";
import { usePagination } from "./../../hooks/usePagination";
import EmptyBox from "../EmptyBox";
import { Grid, Header, LeftColumn, Loader, Tooltip } from "..";
import { CalendarProps } from "./types";
import {
  StyledOuterWrapper,
  StyledInnerWrapper,
  StyledEmptyBoxWrapper,
  Ripples,
} from "./styles";

import { DebouncedFunc } from "lodash";

const initialTooltipData: TooltipData = {
  coords: { x: 0, y: 0 },
  resourceIndex: 0,
  disposition: {
    taken: { hours: 0, minutes: 0 },
    free: { hours: 0, minutes: 0 },
    overtime: { hours: 0, minutes: 0 },
    allocation: { hours: 0, minutes: 0 },
    allocationGroup: { hours: 0, minutes: 0 },
    unavailability: { hours: 0, minutes: 0 },
    projection: { hours: 0, minutes: 0 },
  },
};

export const Calendar: FC<CalendarProps> = ({
  data,
  onTileClick,
  onItemClick,
  toggleTheme,
  topBarWidth,
}) => {
  const [tooltipData, setTooltipData] =
    useState<TooltipData>(initialTooltipData);
  const [filteredData, setFilteredData] = useState(data);
  const [isVisible, setIsVisible] = useState(false);
  const [searchPhrase, setSearchPhrase] = useState("");
  const {
    zoom,
    startDate,
    isLoading,
    config: {
      includeTakenHoursOnWeekendsInDayView,
      showTooltip,
      showThemeToggle,
    },
    listHolidays,
  } = useCalendar();

  const gridRef = useRef<HTMLDivElement>(null);
  const {
    page,
    projectsPerPerson,
    totalRowsPerPage,
    rowsPerItem,
    currentPageNum,
    pagesAmount,
    next,
    previous,
    reset,
  } = usePagination(filteredData);

  const debouncedHandleMouseOver = useRef<DebouncedFunc<
    (
      e: MouseEvent,
      startDate: Day,
      rowsPerItem: number[],
      projectsPerPerson: SchedulerProjectData[][][],
      zoom: ZoomLevel
    ) => void
  > | null>(null);

  useEffect(() => {
    debouncedHandleMouseOver.current = debounce(
      (
        e: MouseEvent,
        startDate: Day,
        rowsPerItem: number[],
        projectsPerPerson: SchedulerProjectData[][][],
        zoom: ZoomLevel,
        dismissedDates: (string | null | undefined)[]
      ) => {
        if (!gridRef.current) return;

        const { left, top } = gridRef.current.getBoundingClientRect();
        const tooltipCoords = { x: e.clientX - left, y: e.clientY - top };

        const {
          coords: { x, y },
          resourceIndex,
          disposition,
        } = getTooltipData(
          startDate,
          tooltipCoords,
          rowsPerItem,
          projectsPerPerson,
          zoom,
          includeTakenHoursOnWeekendsInDayView,
          listHolidays || [],
          dismissedDates
        );

        setTooltipData({ coords: { x, y }, resourceIndex, disposition });
        setIsVisible(true);
      },
      300
    );
  }, [
    startDate,
    rowsPerItem,
    projectsPerPerson,
    zoom,
    listHolidays,
    includeTakenHoursOnWeekendsInDayView,
  ]);

  const debouncedFilterData = useRef(
    debounce((dataToFilter: SchedulerData, enteredSearchPhrase: string) => {
      reset();
      setFilteredData(
        dataToFilter.filter((item) =>
          item.label.title
            .toLowerCase()
            .includes(enteredSearchPhrase.toLowerCase())
        )
      );
    }, 500)
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const phrase = event.target.value;
    setSearchPhrase(phrase);
    debouncedFilterData.current.cancel();
    debouncedFilterData.current(data, phrase);
  };

  const handleMouseLeave = useCallback(() => {
    debouncedHandleMouseOver.current?.cancel();
    setIsVisible(false);
    setTooltipData(initialTooltipData);
  }, []);

  useEffect(() => {
    const dismissedDates = page.map((r) => r.label.dismissedDate);
    const handleMouseOver = (e: MouseEvent) =>
      debouncedHandleMouseOver.current(
        e,
        startDate,
        rowsPerItem,
        projectsPerPerson,
        zoom,
        dismissedDates
      );
    const gridArea = gridRef.current;

    if (!gridArea) return;

    gridArea.addEventListener("mousemove", handleMouseOver);
    gridArea.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      gridArea.removeEventListener("mousemove", handleMouseOver);
      gridArea.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [
    debouncedHandleMouseOver,
    handleMouseLeave,
    projectsPerPerson,
    rowsPerItem,
    startDate,
    zoom,
    listHolidays,
    page,
  ]);

  useEffect(() => {
    if (searchPhrase) return;

    setFilteredData(data);
  }, [data, searchPhrase]);

  return (
    <StyledOuterWrapper>
      <LeftColumn
        data={page}
        pageNum={currentPageNum}
        pagesAmount={pagesAmount}
        rows={rowsPerItem}
        onLoadNext={next}
        onLoadPrevious={previous}
        searchInputValue={searchPhrase}
        onSearchInputChange={handleSearch}
        onItemClick={onItemClick}
      />
      <StyledInnerWrapper>
        <Header
          zoom={zoom}
          topBarWidth={topBarWidth}
          showThemeToggle={showThemeToggle}
          toggleTheme={toggleTheme}
        />
        {isLoading ? (
          <Ripples>
            <Loader isLoading={isLoading} position="left" />
          </Ripples>
        ) : data.length ? (
          <Grid
            data={page}
            zoom={zoom}
            rows={totalRowsPerPage}
            ref={gridRef}
            onTileClick={onTileClick}
          />
        ) : (
          <StyledEmptyBoxWrapper width={topBarWidth}>
            <EmptyBox />
          </StyledEmptyBoxWrapper>
        )}
        {showTooltip && isVisible && tooltipData?.resourceIndex > -1 && (
          <Tooltip tooltipData={tooltipData} zoom={zoom} />
        )}
      </StyledInnerWrapper>
    </StyledOuterWrapper>
  );
};

export default Calendar;
